import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { LOCATION_STATUS_TYPES } from '@lib/insightsService/reporters/LocationEventReporter';
import { renderSomethingWentWrong } from '@utils';
import { useAuth } from '@lib/authentication';
import { useErrorReporter } from '@lib/errorReporter';
import { useForm } from '@hooks';
import { useInsightsService } from '@lib/insightsService';
import { useProfile } from '@lib/profile';
import { USER_AGENT_MESSAGE, VPN_OR_PROXY_LOGIN_ERROR } from '@constants';
import {
  AdaptiveArea,
  Button,
  Icon,
  InvestorLocationModal,
  LegacyLink,
  PasswordField,
  Title
} from '@components';
import {
  AuthenticationAttemptsLimitReached,
  DistanceExceededFromLastConnection,
  InactiveAccount,
  InvalidCredentials,
  InvestorHasAlreadyAnActiveSession,
  UnusualAccess,
  UserAgentRequired
} from '@lib/hundredBricksAPI/authentication';

import useGeolocation from '../../../hooks/useGeolocation';
import { CurrentSessionModal, FailedAttemptsLock, LocationBlockedModal } from '../components';

import {
  AlternativeAction,
  AntiphishingContent,
  AntiphishingData,
  AntiphishingDetails,
  AntiphishingField,
  AntiphishingImage,
  AntiphishingSection,
  AntiphishingWarning,
  ErrorMessage,
  FormContainer,
  ResponsiveLineBreak,
  Text
} from './Steps.styles';

const FORGOT_PASSWORD_URL = '/forgot-password';
const PASSWORD_MINIMUM_LENGTH = 6;
const INCORRECT_PASSWORD_MESSAGE = '¡Vaya! La contraseña que escribiste no es correcta.'
  + ' Intenta de nuevo.';

const stepTwoSchema = {
  password: {
    required: true
  }
};

const StepTwo = props => {
  const {
    email,
    antiphishingData,
    onCancel,
    onSuccess,
    displayUnusualAccess,
    displayInactiveAccount
  } = props;

  const {
    names,
    firstLastName,
    secondLastName,
    imageUrl,
    phrase
  } = antiphishingData;

  const { isLoading: isLoadingProfile } = useProfile();
  const { authenticationEvent, reportInsight, locationEventReporter } = useInsightsService();
  const {
    getCurrentPosition,
    getGeolocationStatus,
    geolocationStatus,
    isGettingLocation
  } = useGeolocation();
  const errorReporter = useErrorReporter();
  const form = useForm({ initialValues: { password: '' }, schema: stepTwoSchema });
  const [lockedByFailedAttemptsData, setLockedByFailedAttemptsData] = useState();
  const [showAlreadyActiveSessionModal, setShowAlreadyActiveSessionModal] = useState(false);
  const [showVPNMessage, setShowVPNMessage] = useState(false);
  const [displayLocationBlockedModal, setDisplayLocationBlockedModal] = useState(false);
  const [blockedReason, setBlockedReason] = useState();
  const [showInvestorLocationModal, setShowInvestorLocationModal] = useState(false);
  const [showUserAgentMessage, setShowUserAgentMessage] = useState(false);
  const [isApprovingLocation, setIsApprovingLocation] = useState(false);
  const [isProcessingToLogin, setIsProcessingToLogin] = useState(false);
  const [isAcquiringLocation, setIsAcquiringLocation] = useState(false);

  const {
    login,
    isAuthenticated,
    isLoading: isAuthenticating,
    error: loginError
  } = useAuth();

  const handleLogin = async () => {
    setIsAcquiringLocation(true);

    getCurrentPosition(coords => {
      setIsAcquiringLocation(false);

      const { latitude, longitude } = coords;

      login(email, form.values.password, latitude, longitude);
    }, reason => {
      const responseStatusByReason = {
        unavailable: LOCATION_STATUS_TYPES.UNAVAILABLE,
        positionUnavailable: LOCATION_STATUS_TYPES.UNAVAILABLE,
        unsupported: LOCATION_STATUS_TYPES.UNAVAILABLE,
        timeout: LOCATION_STATUS_TYPES.TIMEOUT
      };
      const responseStatus = responseStatusByReason[reason] ?? LOCATION_STATUS_TYPES.DENIED;

      setIsAcquiringLocation(false);
      setIsProcessingToLogin(false);
      setBlockedReason(reason);
      setDisplayLocationBlockedModal(true);
      locationEventReporter.sendResponseStatus(responseStatus);
    });

    setIsApprovingLocation(false);
  };

  const acceptShareLocation = () => {
    setShowInvestorLocationModal(false);
    handleLogin();
    locationEventReporter.sendResponseStatus(LOCATION_STATUS_TYPES.ACCEPTED);
  };

  const cancelShareLocation = () => {
    setShowInvestorLocationModal(false);
    setIsApprovingLocation(false);
    setIsProcessingToLogin(false);
    locationEventReporter.sendResponseStatus(LOCATION_STATUS_TYPES.DENIED);
  };

  const signupErrorMessage = (showVPNMessage && VPN_OR_PROXY_LOGIN_ERROR)
    || (showUserAgentMessage && USER_AGENT_MESSAGE)
    || undefined;

  const onSubmit = async () => {
    if (isAcquiringLocation) return;

    if (String(form.values.password).length < PASSWORD_MINIMUM_LENGTH) {
      form.addError({
        field: 'password',
        message: INCORRECT_PASSWORD_MESSAGE
      });

      return;
    }

    setShowVPNMessage(false);
    setShowUserAgentMessage(false);
    setIsApprovingLocation(true);
    setIsProcessingToLogin(true);

    if (geolocationStatus === 'granted') {
      handleLogin();

      return;
    }

    const nextGeolocationStatus = await getGeolocationStatus();

    if (nextGeolocationStatus === 'prompt') {
      setShowInvestorLocationModal(true);
    } else if (nextGeolocationStatus === 'denied') {
      setDisplayLocationBlockedModal(true);
      setIsApprovingLocation(false);
      setIsProcessingToLogin(false);
      locationEventReporter.sendResponseStatus(LOCATION_STATUS_TYPES.BLOCKED);
    } else if (nextGeolocationStatus === 'granted') {
      handleLogin();
    }
  };

  const handleLoginError = error => {
    reportInsight(authenticationEvent.logInError, error);

    if (error instanceof InvalidCredentials) {
      form.addError({
        field: 'password',
        message: INCORRECT_PASSWORD_MESSAGE
      });
    } else if (error instanceof AuthenticationAttemptsLimitReached) {
      setLockedByFailedAttemptsData({
        remainingTimeInMilliseconds: error.getRemainingTime()
      });
    } else if (error instanceof InvestorHasAlreadyAnActiveSession) {
      setShowAlreadyActiveSessionModal(true);
    } else if (error instanceof UnusualAccess) {
      displayUnusualAccess();
    } else if (error instanceof InactiveAccount) {
      const lastSessionAt = error.cause.cause?.response?.data?.metadata?.lastSessionAt;
      displayInactiveAccount(lastSessionAt);
    } else if (error instanceof DistanceExceededFromLastConnection) {
      setShowVPNMessage(true);
    } else if (error instanceof UserAgentRequired) {
      setShowUserAgentMessage(true);
    } else {
      errorReporter.critical(error);
      renderSomethingWentWrong();
    }
  };

  useEffect(() => {
    if (isAuthenticated && onSuccess) {
      onSuccess();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isAuthenticated]);

  useEffect(() => {
    if (loginError) {
      setIsProcessingToLogin(false);
      handleLoginError(loginError);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [loginError]);

  if (lockedByFailedAttemptsData) {
    const timeToUnlock = lockedByFailedAttemptsData.remainingTimeInMilliseconds;

    return (
      <FailedAttemptsLock
        email={email}
        remainingLockTimeInMilliseconds={timeToUnlock}
      />
    );
  }

  function obfuscate(value) {
    if (!value) {
      return '';
    }

    return String(value).charAt(0) + '*'.repeat(value.length - 1);
  }

  const fullName = (
    `${names} ${obfuscate(firstLastName)} ${obfuscate(secondLastName)}`
  ).toLowerCase();

  return (
    <Fragment>
      <Title>Iniciar sesión</Title>

      {Boolean(email) && (
        <AntiphishingSection>
          {Boolean(imageUrl) && (
            <AntiphishingWarning>
              <Icon name="icWarning" alt="warning" />
              <Text>Si no reconoces la imagen, comunícate con nuestro servicio a cliente.</Text>
            </AntiphishingWarning>
          )}

          <AntiphishingContent>
            {Boolean(imageUrl) && (
              <AntiphishingImage src={imageUrl} alt="100 ladrillos" />
            )}

            <AntiphishingDetails>
              {Boolean(phrase) && (
                <AntiphishingData>
                  <Text>Frase</Text>

                  <AntiphishingField>{phrase}</AntiphishingField>
                </AntiphishingData>
              )}

              {Boolean(names) && (
                <AntiphishingData>
                  <Text>Nombre</Text>

                  <AntiphishingField>
                    {fullName}
                  </AntiphishingField>
                </AntiphishingData>
              )}

              {Boolean(!names) && (
                <AntiphishingData>
                  <Text>Correo electrónico</Text>

                  <AntiphishingField>
                    {email}
                  </AntiphishingField>
                </AntiphishingData>
              )}
            </AntiphishingDetails>
          </AntiphishingContent>
        </AntiphishingSection>
      )}

      <FormContainer
        id="login-password"
        className="login-password"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <PasswordField
          id="password"
          focus
          placeholder="Escribe tu contraseña"
          label="¿Cuál es tu contraseña?"
          value={form.values.password}
          {...form.fieldProps('password')}
        />

        {Boolean(signupErrorMessage) && (
          <ErrorMessage>
            {signupErrorMessage}
          </ErrorMessage>
        )}

        <AlternativeAction>
          {'¿Olvidaste tu contraseña? '}
          <ResponsiveLineBreak />
          <LegacyLink to={FORGOT_PASSWORD_URL}>
            Restablece tu contraseña
          </LegacyLink>
          .
        </AlternativeAction>

        <AdaptiveArea>
          <Button
            id="cancel"
            color="secondary"
            onClick={onCancel}
          >
            Anterior
          </Button>
          <Button
            id="login"
            type="submit"
            disabled={!form.isValid()
              || isApprovingLocation
              || isProcessingToLogin
              || isAcquiringLocation}
            isLoading={isAuthenticating
              || isLoadingProfile
              || isApprovingLocation
              || isProcessingToLogin
              || isAcquiringLocation}
          >
            Iniciar sesión
          </Button>
        </AdaptiveArea>
      </FormContainer>

      <CurrentSessionModal
        isOpen={showAlreadyActiveSessionModal}
        onClose={() => setShowAlreadyActiveSessionModal(false)}
      />

      <InvestorLocationModal
        isOpen={showInvestorLocationModal}
        isLoading={isGettingLocation}
        onConfirm={acceptShareLocation}
        onCancel={cancelShareLocation}
      />

      <LocationBlockedModal
        isOpen={displayLocationBlockedModal}
        reason={blockedReason}
        onConfirm={() => {
          setDisplayLocationBlockedModal(false);
          handleLogin();
        }}
      />
    </Fragment>
  );
};

StepTwo.propTypes = {
  email: PropTypes.string.isRequired,
  antiphishingData: PropTypes.shape({
    names: PropTypes.string,
    firstLastName: PropTypes.string,
    secondLastName: PropTypes.string,
    imageUrl: PropTypes.string,
    phrase: PropTypes.string
  }),
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  displayUnusualAccess: PropTypes.func.isRequired,
  displayInactiveAccount: PropTypes.func.isRequired
};

StepTwo.defaultProps = {
  antiphishingData: {}
};

export default StepTwo;
